import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchEmployeeDirectoryData } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryData/fetchEmployeeDirectoryDataApi";
import { EmployeeDirectorySpecialty } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryData/types";
import { Employee } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryProfiles/types";
import { DirectoryTeam } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryTeams/types";

export const ALL_TEAMS_FILTER = "all";

export interface UseEmployeeDirectoryPageReturn {
  loading: boolean;
  members: Employee[];
  filteredMembers: Employee[];

  teams: DirectoryTeam[];
  activeTeamUuid: string;
  setActiveTeamUuid: (uuid: string) => void;

  specialtyOptions: EmployeeDirectorySpecialty[];
  selectedSpecialtyUuids: string[];
  toggleSpecialty: (uuid: string) => void;
  clearSpecialties: () => void;
}

export const useEmployeeDirectoryPage = (): UseEmployeeDirectoryPageReturn => {
  const [members, setMembers] = useState<Employee[]>([]);
  const [teams, setTeams] = useState<DirectoryTeam[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<
    EmployeeDirectorySpecialty[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTeamUuid, setActiveTeamUuid] =
    useState<string>(ALL_TEAMS_FILTER);
  const [selectedSpecialtyUuids, setSelectedSpecialtyUuids] = useState<
    string[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchEmployeeDirectoryData()
      .then((response) => {
        if (cancelled) return;
        setMembers(response.data.profiles);
        setTeams(response.data.teams);
        setSpecialtyOptions(response.data.specialties);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleSpecialty = useCallback((uuid: string) => {
    setSelectedSpecialtyUuids((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
    );
  }, []);

  const clearSpecialties = useCallback(() => {
    setSelectedSpecialtyUuids([]);
  }, []);

  const filteredMembers = useMemo(() => {
    const selectedSpecialtySet = new Set(selectedSpecialtyUuids);
    return members.filter((member) => {
      if (activeTeamUuid !== ALL_TEAMS_FILTER) {
        const inTeam = member.teams.some((t) => t.uuid === activeTeamUuid);
        if (!inTeam) return false;
      }
      if (selectedSpecialtySet.size > 0) {
        const hasAny = member.specialties.some((s) =>
          selectedSpecialtySet.has(s.uuid),
        );
        if (!hasAny) return false;
      }
      return true;
    });
  }, [members, activeTeamUuid, selectedSpecialtyUuids]);

  return {
    loading,
    members,
    filteredMembers,

    teams,
    activeTeamUuid,
    setActiveTeamUuid,

    specialtyOptions,
    selectedSpecialtyUuids,
    toggleSpecialty,
    clearSpecialties,
  };
};
